
/* -----------------------------------------------------------------------------------------------------
  - Created: [20-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains the Employee Report search filter component
------------------------------------------------------------------------------------------------------- */

import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { useFormik } from 'formik';
import '../../ReportsCommonStyles/CommonSearchFilterStyle.css';
import ActionButton from '../../../../Components/custom_components/CustomButtons/ActionButton/ActionButton';
import CustomTextFieldWithIcon from '../../../../Components/custom_components/custom_text_field/CustomTextFieldWithIcon';
import CustomBreadcrumbs from '../../../../Components/custom_components/CustomBreadcrumbs/CustomBreadcrumbs';
import { get_worksite_with_out_pagination } from '../../../../config/Worksite/siteApis';
import CustomSelectDropdown from '../../../../Components/custom_components/CustomSelectDropdown/CustomSelectDropdown';
import { get_technology_with_out_pagination } from '../../../../config/Resource/technologyApis';
import CustomDatePicker from '../../../../Components/custom_components/CustomDatePicker/CustomDatePicker';
import dayjs from 'dayjs';
import { Icons } from '../../../../utils/GlobalIcons';
import ActionButtonWithIcon from '../../../../Components/custom_components/CustomButtons/ActionButtonWithIcon/ActionBtnWithIcon';

function FutureScheduleFilter({
  tableTitle,
  searchFilter,
  on_change_value, on_page_count_update,
  onExportPDF,
  onExportExcel,
  isExportingPDF,
  isExportingExcel
}) {
  const [technologyListing, setTechnologyListing] = useState([]);
  const [worksiteOptions, setWorksiteOptions] = useState([]);

  useEffect(() => {
    fetchTechnologies();
    fetchSites();
  }, []);

  const fetchTechnologies = async () => {
    try {
      const response = await get_technology_with_out_pagination();
      if (response?.status === "success") {
        // Transform data into the desired format with label and value
        const transformedData = response.data.data?.map((item) => ({
          label: item?.TechnologiesName,
          value: item?.TechnologiesTypeId
        }));

        // Set the transformed data to the state
        setTechnologyListing(transformedData || []);
      } else {
        setTechnologyListing([]);
      }
    } catch (error) {
      console.error('Error fetching technologies:', error);
      setTechnologyListing([]);
    }
  };
  const fetchSites = async () => {
    try {
      const response = await get_worksite_with_out_pagination();
      if (response?.status === 'success') {
        const filtered = response.data.data
          .filter(item => item?.ParentWorksiteCode !== null)
          .map(item => ({ label: item.worksiteName, value: item.WorksiteID }));
        setWorksiteOptions(filtered);
      }
    } catch (error) {
      console.error('Failed to load worksites', error);
    }
  };


  const formik = useFormik({
    initialValues: {
      employee_name: '',
      worksite_name: '',
      from_date: '',
      to_date: '',
    },
  });

  const resetFilter = () => {
    formik.resetForm();
    on_change_value(1)
    on_page_count_update(25)
    searchFilter({
      employee_name: '',
      from_date: '',
      to_date: '',
      worksite_name: '',
    });
  };
  const breadcrumbs = ["Reports", tableTitle];
  return (
    <div className="common-search-filter-container">
      <div className="common-search-filter-sub-container">
        <div className="common-search-header">
          <div className="common-search-left">
            <div className="common-table-title">{tableTitle}</div>
            <CustomBreadcrumbs breadcrumbs={breadcrumbs} />
          </div>
          <div className="common-export-buttons-container">
            <ActionButtonWithIcon
              label="Export PDF"
              onClick={onExportPDF}
              icon={Icons.PdfIcon}
              backgroundColor="#ffffff"
              hoverColor="#fef2f2"
              labelColor="#000000"
              borderColor="#e5e7eb"
              iconColor="#dc2626"
              loading={isExportingPDF}
            />

            <ActionButtonWithIcon
              label="Export Excel"
              onClick={onExportExcel}
              icon={Icons.ExcelIcon}
              backgroundColor="#ffffff"
              hoverColor="#f0fdf4"
              labelColor="#000000"
              borderColor="#e5e7eb"
              iconColor="#16a34a"
              loading={isExportingExcel}
            />
          </div>
        </div>

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <CustomTextFieldWithIcon
              id="employee_name"
              name="employee_name"
              placeholder="Search Employee Name"
              value={formik.values.employee_name}
              onChange={formik.handleChange}
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <CustomSelectDropdown
              name="survey"
              placeholder="Survey"
              options={technologyListing}
              value={technologyListing.find((tech) => tech.value === formik.values.survey) || ""}
              onChange={(selectedOption) => {
                formik.setFieldValue("survey", selectedOption ? selectedOption.value : "");
              }}
              onBlur={formik.handleBlur}

              isDisabled={false}
              isMulti={false} // Single select
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <CustomSelectDropdown
              name="worksite_name"
              placeholder="Select Worksite"
              options={worksiteOptions}
              value={worksiteOptions.find(opt => opt.value === formik.values.worksite_name) || ''}
              onChange={opt => formik.setFieldValue('worksite_name', opt?.value || '')}
              onBlur={formik.handleBlur}
              isMulti={false}
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>


            <CustomDatePicker
              name="from_date"
              label="From Date"
              onChange={(date) => {
                formik.setFieldValue("from_date", date);
              }}
              onBlur={formik.handleBlur}
              value={dayjs(formik.values.from_date, "DD-MM-YYYY")}
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <CustomDatePicker
              name="to_date"
              label="End Date"
              onChange={(date) => {
                formik.setFieldValue("to_date", date);
              }}
              onBlur={formik.handleBlur}
              value={dayjs(formik.values.to_date, "DD-MM-YYYY")}
              minDate={dayjs(formik.values.from_date, "DD-MM-YYYY")} // Set minimum date
              width="200px"
            />

          </Grid>

          <Grid item xs={12} sm={12} md={3}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ActionButton
                  onClick={() => {
                    on_change_value(1)
                    searchFilter({ ...formik.values }); // Always trigger search, even if no values changed
                  }}
                  btnDisable={false}
                  label="Search"
                  btnType="primary"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <ActionButton
                  onClick={resetFilter}
                  label="Reset"
                  btnType="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

export default FutureScheduleFilter;